import React from 'react'
import cn from 'classnames'

import Toast from 'src/Toast'
import { CSSSlideFadeTransition } from 'components/CSSTransitions'
import getWindowScreenshot from 'helpers/getWindowScreenshot'

import styles from './styles.module.less'

import iconMessage from './images/icon-message.svg'
import iconClose from './images/icon-close.svg'
import iconChevronRight from './images/icon-chevron-right.svg'
import iconChevronLeft from './images/icon-chevron-left.svg'
import ReactDOM from 'react-dom'

type MenuItem = { type: string, title: string, description: string }

const MENU_ITEMS: Array<MenuItem> = [{
    type: 'bug-report',
    title: 'Report a Bug',
    description: 'Is something not working correctly? Let us know!'
},{
    type: 'improvement-suggestion',
    title: 'Suggest Improvements',
    description: 'Make a suggestion to help improve your experience.'
}, {
    type: 'contact-support',
    title: 'Contact Support',
    description: 'Contact our expert support team.'
}]

const FORM_PLACEHOLDER = {
    'bug-report': 'Describe the issue',
    'improvement-suggestion': 'What can we make better',
    'contact-support': 'How can we help',
}

const SUCCESS_MESSAGE = {
    'bug-report': 'We are sorry to hear about this issue. We have notified our development team, and we are working to fix the issue.',
    'improvement-suggestion': 'Thank you for your suggestion. We are always looking for ways to improve our app for our members.',
    'contact-support': 'We hear you loud and clear. Our support team will be in touch to talk through your submission.',
}

export default function () {
    return ReactDOM.createPortal(
        <UserFeedback/>,
        document.getElementById('js-user-feedback-widget')!
    )
}

function UserFeedback () {
    const [activeForm, setActiveForm] = React.useState<MenuItem | null>(null)
    const [menu, setMenu] = React.useState(false)
    const [submitted, setSubmitted] = React.useState(false)
    const screenshotsRef = React.useRef<Array<Blob>>(null)
    const [menuIsLoading, setIsMenuIsLoading] = React.useState(false)
    const [submitLoading, setSubmitLoading] = React.useState(false)

    const handleSubmit = async (data) => {
        setSubmitLoading(true)
        const body = new FormData()

        for (const screenshot of screenshotsRef.current!) {
            body.append('screenshot[]', screenshot)
        }
        body.append('feedback', data.report)

        const response = await fetch(`/user-feedback/${activeForm!.type}`, {
            method: 'POST',
            body
        })

        if (!response.ok) {
            throw new Error(await response.text())
        }

        setSubmitLoading(false)
        setSubmitted(true)
    }

    const handleMenuToggle = async () => {
        if (submitLoading) return

        if (!menu && !activeForm) {
            setIsMenuIsLoading(true)
            const screenshotPromises: Array<Promise<Blob | Array<Blob>>> = [getWindowScreenshot(window.document)]

            document.querySelectorAll('iframe').forEach(iframe => {
                if (iframe.dataset.html2canvasIgnore) return

                if (iframe.contentDocument !== null) {
                    screenshotPromises.push(getWindowScreenshot(iframe.contentDocument))
                    return
                }

                if ((new URL(iframe.src).hostname).includes('.shiftone.')) {
                    screenshotPromises.push(new Promise<Array<Blob>>((resolve, reject) => {
                        const timeout = setTimeout(() => reject('timeout'), 5000)

                        function listener (e) {
                            if (e.data.type !== 'screenshot') return

                            clearTimeout(timeout)

                            if (e.data.blobs) {
                                resolve(e.data.blobs)
                                return
                            }

                            reject('error')
                            window.removeEventListener('message', listener)
                        }

                        window.addEventListener('message', listener)

                        iframe.contentWindow!.postMessage({ type: 'screenshot' }, { targetOrigin: '*' })
                    }))
                }
            })

            try {
                screenshotsRef.current = (await Promise.allSettled(screenshotPromises))
                    .filter(p => p.status === 'fulfilled')
                    .map((p: PromiseSettledResult<Blob|Array<Blob>>) => (p as PromiseFulfilledResult<Blob|Array<Blob>>).value)
                    .flat()
            } catch {
                alert('Failed to generate screenshots.')
            }
            setIsMenuIsLoading(false)
        }

        if (submitted) {
            setActiveForm(null)
            setSubmitted(false)
        }

        setMenu(!menu)
    }

    return (
        <div data-testid="userfeedback-container" className={styles.wrapper}>
            <div onClick={handleMenuToggle} className={cn(styles.backdrop, { [styles.active]: menu })}/>

            <div className={styles.innerWrapper}>
                <CSSSlideFadeTransition in={menu && !activeForm} unmountOnExit>
                    <div className={styles.content}>
                        <div className={styles.menu}>
                            {Object.values(MENU_ITEMS).map(menuItem => (
                                <button onClick={() => { setActiveForm(menuItem) }} key={menuItem.type} className={styles.menuItem}>
                                    <div className={styles.buttonContent}>
                                        <p className={styles.menuItemTitle}>{menuItem.title}</p>
                                        <p className={styles.menuItemDescription} dangerouslySetInnerHTML={{ __html: menuItem.description}}/>
                                    </div>

                                    <div className={styles.buttonIcon}>
                                        <img src={iconChevronRight} alt="Icon Right"/>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </CSSSlideFadeTransition>

                {menu && activeForm && (
                    <div className={styles.content}>
                        <Form
                            type={activeForm?.type}
                            title={activeForm?.title}
                            placeholder={FORM_PLACEHOLDER[activeForm?.type]}
                            onSubmit={handleSubmit}
                            onBack={() => { setActiveForm(null) }}
                        />
                    </div>
                )}

                <button
                    data-testid="userfeedback-toggle"
                    className={cn(styles.toggleButton, {
                        [styles.loading]: menuIsLoading,
                        [styles.disabled]: submitLoading,
                    })}
                    onClick={handleMenuToggle}
                >
                    <img className={cn({ [styles.active]: menu && !menuIsLoading })} src={iconClose} alt="Icon"/>
                    <img className={cn({ [styles.active]: !menu && !menuIsLoading })} src={iconMessage} alt="Icon"/>
                </button>
            </div>
        </div>
    )
}

type FormProps = {
    type: string,
    title: string,
    placeholder: string,
    onSubmit: (data: { report: string }) => void,
    onBack: () => void,
}

function Form(props: FormProps) {
    const [submitLoading, setSubmitLoading] = React.useState(false)
    const [successMessage, setSuccessMessage] = React.useState<string | null>(null)
    const reportDescriptionRef = React.useRef<HTMLTextAreaElement>(null)

    const handleSubmit = async () => {
        if (reportDescriptionRef.current) {
            try {
                setSubmitLoading(true)
                await props.onSubmit({ report: reportDescriptionRef.current.value })
                setSuccessMessage(SUCCESS_MESSAGE[props.type])
            } catch {
                Toast.error({
                    content: 'Something went wrong when trying to submit your feedback.',
                    timeout: 10
                })
            }

            setSubmitLoading(false)
        }
    }

    React.useEffect(() => {
        setSuccessMessage(null)
    }, [])

    return (
        <div className={styles.formWrapper}>
            <div className={styles.formHeader}>
                <img
                    className={cn(styles.backButton, { [styles.disabled]: submitLoading })}
                    onClick={() => { props.onBack() }}
                    src={iconChevronLeft}
                    alt="Back"
                />
                <p className={styles.cardTitle}>{props.title}</p>
            </div>

            {successMessage ? (
                <p className={styles.successMessage}>{successMessage}</p>
            ) : (
                <>
                    <textarea placeholder={props.placeholder} className={styles.reportTextarea} ref={reportDescriptionRef} rows={7}></textarea>

                    <div className={styles.formFooter}>
                        <button onClick={handleSubmit} className={cn(styles.submitButton, { [styles.loading]: submitLoading })}>Send</button>
                    </div>
                </>
            )}
        </div>
    )
}
